import { useState, useEffect } from "react";
import { ArrowSquareOut, Newspaper } from "@phosphor-icons/react";
import { useGetInvolvedModal } from "@/contexts/GetInvolvedModalContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function NewsPage() {
  const { openModal } = useGetInvolvedModal();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/news`)
      .then((r) => r.json())
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ backgroundColor: "#F7F5F0", minHeight: "100vh" }}>

      {/* Hero */}
      <section className="pt-20 pb-16 px-6 md:px-12" style={{ backgroundColor: "#1A5C37" }}>
        <div className="max-w-4xl mx-auto text-center">
          <span
            className="inline-block text-xs font-medium tracking-widest uppercase mb-4 px-3 py-1 rounded-full"
            style={{ backgroundColor: "rgba(247,245,240,0.15)", color: "rgba(247,245,240,0.8)" }}
          >
            Media Coverage
          </span>
          <h1
            className="font-heading text-4xl sm:text-5xl font-semibold mb-4 leading-tight"
            style={{ color: "#F7F5F0" }}
          >
            News
          </h1>
          <p className="text-base max-w-2xl mx-auto" style={{ color: "rgba(247,245,240,0.75)" }}>
            Media coverage on Bengaluru&apos;s Lakes, Environmental Issues and Restoration Initiatives
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div
              className="w-8 h-8 border-2 rounded-full animate-spin"
              style={{ borderColor: "#1A5C37", borderTopColor: "transparent" }}
            />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
              style={{ backgroundColor: "rgba(26,92,55,0.08)" }}
            >
              <Newspaper size={32} weight="duotone" style={{ color: "#1A5C37" }} />
            </div>
            <p className="text-base font-medium" style={{ color: "#1C211F" }}>No news articles yet</p>
            <p className="text-sm mt-1.5" style={{ color: "#4A5450" }}>
              Check back soon for media coverage of KYBIA's work.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {items.map((item) => (
              <a
                key={item.id}
                href={item.news_link}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col rounded-2xl overflow-hidden border transition-all duration-200"
                style={{ backgroundColor: "#fff", borderColor: "rgba(26,92,55,0.1)" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "rgba(26,92,55,0.3)";
                  e.currentTarget.style.boxShadow = "0 8px 32px rgba(26,92,55,0.1)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(26,92,55,0.1)";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
                data-testid={`news-card-${item.id}`}
              >
                {/* Publication logo */}
                <div
                  className="flex items-center justify-center px-6 py-5 border-b"
                  style={{
                    backgroundColor: "rgba(247,245,240,0.6)",
                    borderColor: "rgba(26,92,55,0.07)",
                    minHeight: "80px",
                  }}
                >
                  <img
                    src={`${BACKEND_URL}${item.publication_logo_url}`}
                    alt={item.publication_name}
                    className="max-h-10 max-w-full object-contain"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.parentNode.querySelector(".logo-fallback").style.display = "flex";
                    }}
                  />
                  <div
                    className="logo-fallback hidden items-center gap-2"
                    style={{ color: "#1A5C37" }}
                  >
                    <Newspaper size={20} weight="duotone" />
                    <span className="text-sm font-medium">{item.publication_name}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="flex flex-col flex-1 p-5">
                  {/* Publication name */}
                  <p className="text-xs font-semibold tracking-wide uppercase mb-2" style={{ color: "#1A5C37" }}>
                    {item.publication_name}
                  </p>

                  {/* Title */}
                  <p
                    className="text-sm font-medium leading-snug flex-1 mb-4 line-clamp-4"
                    style={{ color: "#1C211F" }}
                  >
                    {item.title}
                  </p>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-3" style={{ borderTop: "1px solid rgba(26,92,55,0.07)" }}>
                    <span className="text-xs" style={{ color: "#7A8A84" }}>
                      {formatDate(item.created_at)}
                    </span>
                    <span
                      className="flex items-center gap-1 text-xs font-medium transition-colors"
                      style={{ color: "#1A5C37" }}
                    >
                      Read more
                      <ArrowSquareOut size={12} weight="bold" />
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 text-center" style={{ backgroundColor: "#1C211F" }}>
        <p className="text-xs font-medium tracking-widest uppercase mb-3" style={{ color: "rgba(247,245,240,0.5)" }}>
          Stay Connected
        </p>
        <h2 className="font-heading text-2xl font-semibold mb-3" style={{ color: "#F7F5F0" }}>
          Want to stay updated on our work?
        </h2>
        <p className="text-sm max-w-xl mx-auto mb-7" style={{ color: "rgba(247,245,240,0.65)" }}>
          Get involved with KYBIA and be the first to know about our campaigns, research, and media coverage.
        </p>
        <button
          onClick={openModal}
          className="px-7 py-3 rounded-full text-sm font-medium transition-colors"
          style={{ backgroundColor: "#B85C38", color: "#F7F5F0" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#964B2E")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#B85C38")}
        >
          Get Involved
        </button>
      </section>
    </div>
  );
}
